import { Button } from '@shared/components/ui/button';
import { Label } from '@shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { useToast } from '@shared/hooks/use-toast';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUsers } from '../../../CreateEdit/hooks';
import { useUpdateCommercial, useUpdateMarketingLang } from '../../hooks';
import type { CustomerOrigin } from '../../adapters/ClientCommercialApiAdapter';

const EMPTY_ORIGIN_VALUE = '__none__';

const CUSTOMER_ORIGIN_VALUES: CustomerOrigin[] = [
  'organic',
  'student',
  'affiliate',
  'meta_ads',
  'linkedin_ads',
  'google_ads',
  'linkedin',
  'instagram',
  'blog',
  'instagram_prospecting',
  'linkedin_prospecting',
  'cold_email',
  'cold_calling',
  'referral',
  'unknown',
];

interface EditCommercialModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: number;
  initialCommercialId: number | null;
  initialCustomerOrigin: CustomerOrigin | null;
  onSuccess?: () => void;
  initialMarketingLang?: 'ES' | 'EN' | null;
  initialBeepingTeams: any;
  initialBeepingTeamContacts: any[];
}

export const EditCommercialModal = ({
  open,
  onOpenChange,
  teamId,
  initialCommercialId,
  initialCustomerOrigin,
  onSuccess,
  initialMarketingLang,
  initialBeepingTeams,
  initialBeepingTeamContacts,
}: EditCommercialModalProps) => {
  const { t } = useTranslation('viewClientModule');
  const { toast } = useToast();
  const { users, loading: usersLoading } = useUsers();
  const { updateCommercialAsync, isPending: isCommercialPending, reset } = useUpdateCommercial(teamId);
  const { updateMarketingLangAsync, isPending: isMarketingPending } = useUpdateMarketingLang(teamId);

  const [commercialId, setCommercialId] = useState<string>(initialCommercialId ? initialCommercialId.toString() : '');
  const [marketingLang, setMarketingLang] = useState<'ES' | 'EN' | ''>(initialMarketingLang ?? '');
  const [customerOrigin, setCustomerOrigin] = useState<string>(initialCustomerOrigin ?? EMPTY_ORIGIN_VALUE);

  // Reset form when modal opens with new data
  useEffect(() => {
    if (open) {
      setCommercialId(initialCommercialId ? initialCommercialId.toString() : '');
      setMarketingLang(initialMarketingLang ?? '');
      setCustomerOrigin(initialCustomerOrigin ?? EMPTY_ORIGIN_VALUE);
    }
  }, [open, initialCommercialId, initialMarketingLang, initialCustomerOrigin]);

  const userOptions = users.map((user) => ({
    label: user.name,
    value: user.id.toString(),
  }));

  const handleClose = () => {
    setCommercialId(initialCommercialId ? initialCommercialId.toString() : '');
    setMarketingLang(initialMarketingLang ?? '');
    setCustomerOrigin(initialCustomerOrigin ?? EMPTY_ORIGIN_VALUE);
    reset();
    onOpenChange(false);
  };

  const effectiveCustomerOrigin = customerOrigin === EMPTY_ORIGIN_VALUE ? '' : customerOrigin;
  const initialOrigin = initialCustomerOrigin ?? '';

  const hasCommercialChange = commercialId
    ? commercialId !== (initialCommercialId?.toString() ?? '')
    : !!initialCommercialId;
  const hasOriginChange = effectiveCustomerOrigin !== initialOrigin;
  const hasMarketingLangChange = marketingLang !== (initialMarketingLang ?? '');

  const hasChanges = hasCommercialChange || hasOriginChange || hasMarketingLangChange;

  const handleSubmit = async () => {
    if (!hasChanges || !marketingLang) return;

    try {
      // Llamada a la API para actualizar el comercial y origen del cliente
      await updateCommercialAsync({
        ...(commercialId && { commercial_id: parseInt(commercialId, 10) }),
        ...(effectiveCustomerOrigin !== initialOrigin && {
          customer_origin: effectiveCustomerOrigin ? (effectiveCustomerOrigin as CustomerOrigin) : null,
        }),
      });

      // Llamada a la API para actualizar el idioma de marketing (endpoint distinto)
      await updateMarketingLangAsync({
        beepingTeams: initialBeepingTeams,
        beepingTeamContacts: initialBeepingTeamContacts,
        marketingLang,
      });

      // La API responde 204 No Content en caso de éxito
      toast({
        title: t('editCommercial.success.title'),
        description: t('editCommercial.success.description'),
        variant: 'default',
      });

      onSuccess?.();
      handleClose();
    } catch (error: any) {
      // Intentar obtener el mensaje más descriptivo posible desde la API
      const apiMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message;

      toast({
        title: t('editCommercial.error.title'),
        description: apiMessage || t('editCommercial.error.description'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('editCommercial.modal.title')}</DialogTitle>
          <DialogDescription>{t('editCommercial.modal.description')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>{t('fields.commercialAssigned')}</Label>
            <Combobox
              options={userOptions}
              placeholder={t('editCommercial.placeholders.commercial')}
              value={commercialId}
              setValue={setCommercialId}
              className="w-full"
              disabled={usersLoading}
            />
          </div>
          <div className="grid gap-2">
            <Label>{t('fields.marketingLang')}</Label>
            <Combobox
              options={[
                { label: 'ES', value: 'ES' },
                { label: 'EN', value: 'EN' },
              ]}
              placeholder={t('editCommercial.placeholders.marketingLang')}
              value={marketingLang}
              setValue={(value) => setMarketingLang(value as 'ES' | 'EN' | '')}
              className="w-full"
            />
          </div>
          <div className="grid gap-2">
            <Label>{t('fields.customerOrigin')}</Label>
            <Select value={customerOrigin} onValueChange={setCustomerOrigin}>
              <SelectTrigger>
                <SelectValue placeholder={t('editCommercial.placeholders.customerOrigin')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={EMPTY_ORIGIN_VALUE}>{t('placeholders.noData')}</SelectItem>
                {CUSTOMER_ORIGIN_VALUES.map((origin) => (
                  <SelectItem key={origin} value={origin}>
                    {t(`customerOrigins.${origin}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isCommercialPending || isMarketingPending}>
            {t('editCommercial.buttons.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isCommercialPending || isMarketingPending || !hasChanges || !marketingLang}
          >
            {isCommercialPending || isMarketingPending
              ? t('editCommercial.buttons.saving')
              : t('editCommercial.buttons.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
